import { useCallback, useState } from "react";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { parseCsvFile } from "src/services/dataTransfer";

export type BulkIsValidCallback = (data: any[]) => boolean;
export type BulkOnSuccessCallback = (data: any[]) => Promise<void>;

type UploadState = "idle" | "parsing" | "uploading" | "success" | "error";

export const useBulkUploadInput = (
  isValid: BulkIsValidCallback,
  onSuccess: BulkOnSuccessCallback
) => {
  const toast = useAvelaToast();
  const [uploadState, setUploadState] = useState<UploadState>("idle");

  const parseFile = useCallback(
    async (file: File) => {
      const loadingToastId = toast({
        id: "bulk-upload-loading",
        title: "Working hard",
        description: "Importing data...",
        status: "loading",
        duration: null,
        isClosable: false,
      });

      try {
        setUploadState("parsing");

        await new Promise<void>((resolve, reject) => {
          parseCsvFile(
            file,
            async (results: any) => {
              try {
                if (!isValid(results.data)) {
                  setUploadState("error");
                  reject(new Error("Invalid csv format"));
                  return;
                }

                setUploadState("uploading");
                await onSuccess(results.data);
                setUploadState("success");
                resolve();
              } catch (uploadError: any) {
                setUploadState("error");
                reject(uploadError);
              }
            },
            (parseError: any) => {
              console.error(parseError);
              setUploadState("error");
              reject(parseError);
            }
          );
        });

        // Success
        toast.close(loadingToastId);
        toast({
          id: "bulk-upload-success",
          title: "Data imported successfully",
          status: "success",
          isClosable: true,
        });
        setUploadState("idle");
      } catch (err) {
        // Error
        toast.close(loadingToastId);
        toast.error({
          id: "bulk-upload-error",
          title: "Something went wrong!",
          description: "Check the file and try again.",
        });
        setUploadState("idle");
      }
    },
    [isValid, onSuccess, toast]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget?.files
      ? event.currentTarget.files[0]
      : undefined;
    if (file === undefined) return;
    event.currentTarget.value = "";
    parseFile(file);
  };

  return {
    parseFile,
    handleInputChange,
    isUploading: uploadState === "parsing" || uploadState === "uploading",
  };
};
